import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native';

import { DateInput } from '@/components/form/date-input';
import { GlCodeTypeahead } from '@/components/search/gl-code-typeahead';
import { MoneyTextInput } from '@/components/ui/money-text-input';
import { useItems, useWriteOffCharge, useWriteOffInvoice } from '@/hooks/use-accounting';
import { GlCodeAccountType } from '@/types/models';
import type { GlCode, Item } from '@/types/models';
import { todayBusinessIso } from '@/utils/date-utils';
import { formatMoneyCents } from '@/utils/format-money';

/** Item whose GL is the default write-off GL — mirrors WRITE_OFF_ITEM_CODE in api/accounting/write-off.ts. */
export const WRITE_OFF_ITEM_CODE = 'WRITE OFF';

/** Mirrors the server's WRITE_OFF_GL_TYPES: an expense (bad debt), or income for a plain forgive. */
const isWriteOffGl = (g: GlCode): boolean =>
	g.accountType === GlCodeAccountType.Expense
	|| g.accountType === GlCodeAccountType.Income
	|| g.accountType === GlCodeAccountType.OtherIncome;

const parseAmountToCents = (s: string): number | null => {
	const n = Number(s.replace(/[^0-9.-]/g, ''));
	if (!isFinite(n) || n <= 0) return null;
	return Math.round(n * 100);
};

const parseIsoDate = (s: string): number | null => {
	const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!m) return null;
	const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	return isFinite(ms) ? ms : null;
};

/**
 * Payments already applied that a write-off of `amountCents` would release (and the server
 * re-applies FIFO to other open charges): whatever the write-off needs beyond the open remainder.
 */
export const releasedByWriteOff = (amountCents: number, chargeAmountCents: number, paidCents: number): number =>
	Math.max(0, amountCents - Math.max(0, chargeAmountCents - paidCents));

export interface WriteOffChargeFormProps {
	/** A single charge (amount editable), or a whole invoice (every line, full amount). */
	charge?: { id: string; amountCents: number; paidCents?: number };
	invoice?: { id: string; totalCents: number; paidCents: number };
	/** Accounting writers may back-date; everyone else writes off as of today (ET). */
	canEditDate: boolean;
	/** The association reports on a cash basis — show the one-line re-split note. */
	cashBasis?: boolean;
	onDone: () => void;
	onCancel: () => void;
}

/**
 * Write off a charge — or every line of an invoice — (#682): DR the picked GL (default: the WRITE
 * OFF item's GL, normally 7900 BAD DEBT) / CR AR. The charge's own income posting stays. The amount
 * defaults to the FULL charge: payments are auto-applied, so money already on it is released and
 * re-applied to the account's other open charges — the form says how much before confirming.
 */
export const WriteOffChargeForm: React.FC<WriteOffChargeFormProps> = ({ charge, invoice, canEditDate, cashBasis, onDone, onCancel }) => {
	const fullCents = invoice ? invoice.totalCents : (charge?.amountCents ?? 0);
	const paidCents = invoice ? invoice.paidCents : (charge?.paidCents ?? 0);
	const [amount, setAmount] = useState((fullCents / 100).toFixed(2));
	const [dateStr, setDateStr] = useState(todayBusinessIso());
	const [memo, setMemo] = useState('');
	const [glCodeId, setGlCodeId] = useState<string | null>(null);
	const [glTouched, setGlTouched] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const writeOffCharge = useWriteOffCharge();
	const writeOffInvoice = useWriteOffInvoice();
	const pending = writeOffCharge.isPending || writeOffInvoice.isPending;

	const { data: itemsResp } = useItems({ q: WRITE_OFF_ITEM_CODE });
	const defaultGlId = useMemo(
		() => ((itemsResp?.data ?? []) as Item[]).find((i) => i.itemCode === WRITE_OFF_ITEM_CODE)?.glCodeId ?? null,
		[itemsResp],
	);
	// Seed the default once it loads, unless the user already picked (or cleared) a GL.
	useEffect(() => {
		if (!glTouched && defaultGlId) setGlCodeId(defaultGlId);
	}, [defaultGlId, glTouched]);

	const cents = invoice ? fullCents : parseAmountToCents(amount);
	const releasedCents = cents ? releasedByWriteOff(cents, fullCents, paidCents) : 0;

	const submit = async () => {
		if (!cents) { setError('Enter a positive dollar amount.'); return; }
		if (cents > fullCents) { setError(`Amount can't exceed the charge of ${formatMoneyCents(fullCents)}.`); return; }
		const writtenOffOn = parseIsoDate(dateStr);
		if (writtenOffOn == null) { setError('Enter a valid date.'); return; }
		if (!glCodeId) { setError('Pick the GL to write this off to.'); return; }
		setError(null);
		const common = {
			glCodeId,
			...(canEditDate ? { writtenOffOn } : {}),
			...(memo.trim() ? { memo: memo.trim() } : {}),
		};
		try {
			if (invoice) await writeOffInvoice.mutateAsync({ id: invoice.id, body: common });
			else if (charge) await writeOffCharge.mutateAsync({ id: charge.id, body: { ...common, amountCents: cents } });
			onDone();
		} catch (e) {
			setError((e as Error)?.message || 'Failed to write off.');
		}
	};

	return (
		<View testID="write-off-form">
			<Text style={styles.intro}>
				{invoice
					? `Writes off every line of this invoice in full (${formatMoneyCents(fullCents)}).`
					: `Charge ${formatMoneyCents(fullCents)}.`}
				{' '}The income stays on the books; the amount written off moves to the GL below and leaves the receivable.
			</Text>
			{!invoice && (
				<>
					<Text style={styles.label}>Amount to write off</Text>
					<MoneyTextInput
						style={styles.input}
						value={amount}
						onChangeText={setAmount}
						testID="write-off-amount"
					/>
				</>
			)}
			{releasedCents > 0 && (
				<Text style={styles.release} testID="write-off-release-note">
					{`${formatMoneyCents(releasedCents)} of payments/credits applied to this ${invoice ? 'invoice' : 'charge'} will be `
						+ 're-applied to other open charges (any left over stays as account credit).'}
				</Text>
			)}
			{cashBasis && releasedCents > 0 && (
				<Text style={styles.cashNote} testID="write-off-cash-basis-note">
					Cash-basis reports for past months may re-split income between GLs.
				</Text>
			)}
			<DateInput
				label={canEditDate ? 'Written off on' : 'Written off on (requires admin / accounting)'}
				disabled={!canEditDate}
				value={/^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? new Date(`${dateStr}T05:00:00Z`) : null}
				onChange={(d) => setDateStr(d
					? `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
					: todayBusinessIso())}
			/>
			<View style={styles.typeaheadWrap}>
				<Text style={styles.label}>Write off to (GL)</Text>
				<GlCodeTypeahead
					value={glCodeId}
					onSelect={(g) => { setGlTouched(true); setGlCodeId(g?.id ?? null); }}
					filter={isWriteOffGl}
					placeholder="Search expense or income codes…"
					testID="write-off-gl-code"
				/>
				<Text style={styles.hint}>
					Bad debt (expense) for an uncollectible charge; the charge&apos;s income code to forgive it instead.
				</Text>
			</View>
			<Text style={styles.label}>Memo</Text>
			<TextInput
				style={styles.input}
				value={memo}
				onChangeText={setMemo}
				placeholder="Write-off: <charge memo>"
				placeholderTextColor="#bdbdbd"
			/>
			{error && <Text style={styles.error}>{error}</Text>}
			<View style={styles.footerRow}>
				<Pressable style={styles.secondary} disabled={pending} onPress={onCancel}>
					<Text style={styles.secondaryLabel}>Cancel</Text>
				</Pressable>
				<Pressable
					style={[styles.primary, pending && styles.disabled]}
					disabled={pending}
					onPress={() => submit()}
					testID="write-off-submit"
				>
					<Text style={styles.primaryLabel}>{pending ? 'Writing off…' : 'Write off'}</Text>
				</Pressable>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	intro: { fontSize: 13, color: '#455a64', marginBottom: 4 },
	release: { fontSize: 13, color: '#6d4c41', backgroundColor: '#fff8e1', padding: 8, borderRadius: 6, marginTop: 4 },
	label: { fontSize: 12, fontWeight: '600', color: '#666', textTransform: 'uppercase', marginTop: 12, marginBottom: 6 },
	input: { borderWidth: 1, borderColor: '#ddd', borderRadius: 6, padding: 10, fontSize: 14, marginBottom: 8, backgroundColor: '#fff' },
	hint: { fontSize: 12, color: '#78909c', marginTop: 4 },
	cashNote: { fontSize: 12, color: '#90a4ae', marginTop: 4 },
	typeaheadWrap: { position: 'relative' },
	error: { color: '#c62828', marginTop: 8, fontSize: 13 },
	footerRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 16 },
	primary: { flex: 1, backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center' },
	primaryLabel: { color: '#fff', fontWeight: '600' },
	secondary: { backgroundColor: '#fff', padding: 12, borderRadius: 6, alignItems: 'center', paddingHorizontal: 14, borderWidth: 1, borderColor: '#1976d2' },
	secondaryLabel: { color: '#1976d2', fontWeight: '600' },
	disabled: { opacity: 0.5 },
});
